import API_BASE_URL from '../config/apiConfig';
import { toast } from 'react-toastify';

/**
 * Enhanced fetch wrapper with better error handling and automatic token inclusion
 */
export const fetchApi = async (endpoint, options = {}) => {
    const PORT = process.env.PORT || 9090;
    const token = sessionStorage.getItem('token');
    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
    };

    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

    try {
        const response = await fetch(url, { ...options, headers });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));

            // Handle Permission Denied Globally
            if (response.status === 403) {
                toast.warn("Permission Denied: You do not have access to perform this action.");
            }

            const error = new Error(errorBody.message || `Server responded with ${response.status}`);
            error.status = response.status;
            error.code = errorBody.code;
            throw error;
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            console.error("Network Error: Could not connect to the API at", url);
            toast.error("Could not connect to the server. Please check if the backend is running on port 9090.");
        }
        throw error;
    }
};

export const getFileUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('http')) return path;
    const cleanPath = (typeof path === 'string' && path.startsWith('/')) ? path.substring(1) : path;
    const relativePath = (typeof cleanPath === 'string' && cleanPath.startsWith('uploads/')) ? cleanPath.substring(8) : cleanPath;
    return `${API_BASE_URL}/uploads/${relativePath}`;
};

/**
 * Build a publicly shareable PDF URL for WhatsApp / SMS.
 *
 * Priority order:
 *  1. publicUrl  – Full URL returned by the server when PUBLIC_URL is set in .env
 *                  (e.g. ngrok tunnel). Works from any network. ← BEST
 *  2. Local/LAN  – nip.io domain wrapping the LAN IP + port 9090.
 *                  Only works when customer is on the same Wi-Fi. ← FALLBACK
 *  3. Production – https://shreesamrudh.com/uploads/... served by Nginx. ← PROD
 *
 * HOW TO ENABLE FOR LOCAL DEVELOPMENT:
 *  a) Install ngrok:  https://ngrok.com/download
 *  b) Run:  ngrok http 9090
 *  c) Copy the Forwarding URL, e.g. https://abc123.ngrok-free.app
 *  d) Add to asencia-backend/.env:  PUBLIC_URL=https://abc123.ngrok-free.app
 *  e) Restart nodemon — done! PDFs will now be accessible from any network.
 *
 * @param {string}      fileUrl   – relative path, e.g. "uploads/chat/file.pdf"
 * @param {string}      serverIp  – LAN IP from upload API, e.g. "192.168.1.17"
 * @param {string|null} publicUrl – Full public URL from server (when PUBLIC_URL is set)
 */
export const buildPdfShareLink = (fileUrl, serverIp, publicUrl) => {
    // 1. Use server-provided public URL (ngrok) if available — works from any network
    if (publicUrl) return publicUrl;

    // Strip any leading slash
    const cleanFile = fileUrl.replace(/^\/+/, '');

    const isLocal =
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.match(/^192\.168\./) ||
        window.location.hostname.match(/^10\./) ||
        window.location.hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./);

    if (isLocal) {
        // 2. LAN fallback: nip.io makes WhatsApp render the URL as a clickable link
        //    but only works when customer is on the SAME Wi-Fi network.
        const ip = (serverIp && serverIp !== '127.0.0.1')
            ? serverIp
            : (window.location.hostname === 'localhost' ? '127.0.0.1' : window.location.hostname);
        return `http://${ip}.nip.io:9090/${cleanFile}`;
    } else {
        // 3. Production: Nginx serves /uploads as static files
        const serverBase = API_BASE_URL.replace('/api', '');
        return `${serverBase}/${cleanFile}`;
    }
};

export default fetchApi;
